import { useApplicationRejected } from "@/features/admin/hook/useApplicationRejected";
import { RejectApplicationTable } from "@/features/admin/RejectApplicationTable";
import { Button, Calendar, Dropdown, Grid, Input, Typography } from "antd";
import { useEffect, useState } from "react";
import { ArrowDownToLine, ChevronDown, RotateCcw, Search } from "lucide-react";
import { useDebounce } from "@/hooks/use-debounce";
import { CATEGORY_ITEMS } from "@/constants/category";
import dayjs from "dayjs";
import { transformApplicationSearch } from "@/utils/applicationStatus";
import { downloadExcel } from "@/utils/downloadExcel";
import LoadingFullScreen from "@/components/ui/LoadingFullScreen";

const { Title } = Typography;
const { useBreakpoint } = Grid;

function renderDownloadIcon() {
  return <ArrowDownToLine size={18} aria-hidden="true" />;
}

function renderArrowDownIcon() {
  return <ChevronDown size={18} aria-hidden="true" />;
}

export default function RejectApplication() {
  const screens = useBreakpoint();
  const { fetchRejectedList, loading, applicationRejectedList } =
    useApplicationRejected();
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [tableResetKey, setTableResetKey] = useState(0);

  const debouncedSearch = useDebounce(search, 300);
  const pageSize = 25;

  const categoryKey = selectedCategory === "" ? "" : selectedCategory;
  const searchValue = transformApplicationSearch(debouncedSearch);

  useEffect(() => {
    setCurrentPage(1);

    fetchRejectedList(
      searchValue,
      1,
      pageSize,
      categoryKey,
      selectedDate ?? "",
    );
  }, [searchValue]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);

    fetchRejectedList(
      searchValue,
      page,
      pageSize,
      categoryKey,
      selectedDate ?? "",
    );
  };

  const handleCategoryChange = (key: string) => {
    setSelectedCategory(key);
    setCurrentPage(1);

    fetchRejectedList(
      searchValue,
      1,
      pageSize,
      key === "" ? "" : key,
      selectedDate ?? "",
    );
  };

  const handleResetFilters = () => {
    setSearch("");
    setSelectedCategory("");
    setSelectedDate(null);
    setCurrentPage(1);
    setIsDateDropdownOpen(false);
    setTableResetKey((prev) => prev + 1);

    fetchRejectedList("", 1, pageSize, "", "");
  };

  const selectedCategoryLabel =
    CATEGORY_ITEMS.find((item) => item?.key === selectedCategory)?.label ??
    "Semua kategori";

  const handleDownloadExcel = async () => {
    try {
      setDownloadLoading(true);

      await Promise.all([
        downloadExcel(
          applicationRejectedList?.excelUrl,
          "permohonan-ditolak.xlsx",
        ),
        new Promise((resolve) => setTimeout(resolve, 800)),
      ]);
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <>
      <LoadingFullScreen
        visible={downloadLoading}
        message="Sedang memuat turun Excel..."
      />

      <section className="px-4 sm:px-10 py-6 flex flex-col">
      <Title
        level={2}
        style={{
          fontWeight: 700,
          marginBottom: 0,
          fontSize: screens.sm ? 24 : 18,
          color: "var(--color-primary)",
        }}
      >
        Permohonan Ditolak
      </Title>

      <div className="flex items-center flex-wrap justify-between gap-4 mt-4">
        <div className="flex gap-2.5 items-center">
          <Button
            icon={renderDownloadIcon()}
            type="primary"
            size="large"
            className="min-w-48"
            loading={downloadLoading}
            onClick={handleDownloadExcel}
          >
            Muat Turun Excel
          </Button>

          {/* <Button
            icon={renderDownloadIcon()}
            type="primary"
            size="large"
            className="min-w-48"
          >
            Muat Turun PDF
          </Button> */}
        </div>

        <div className="flex gap-2.5 items-center">
          <Dropdown
            trigger={["click"]}
            open={isDateDropdownOpen}
            onOpenChange={setIsDateDropdownOpen}
            popupRender={() => (
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="w-80">
                  <Calendar
                    fullscreen={false}
                    disabledDate={(current) => current.isAfter(dayjs(), "day")}
                    onSelect={(date) => {
                      const formattedDate = date.format("YYYY-MM-DD");

                      setSelectedDate(formattedDate);
                      setCurrentPage(1);

                      fetchRejectedList(
                        searchValue,
                        1,
                        pageSize,
                        categoryKey,
                        formattedDate,
                      );

                      // Hide calendar after selecting date
                      setIsDateDropdownOpen(false);
                    }}
                  />

                  <div className="border-t px-4 py-2 flex justify-end">
                    <Button
                      type="link"
                      onClick={() => {
                        setSelectedDate(null);
                        setCurrentPage(1);

                        fetchRejectedList(
                          searchValue,
                          1,
                          pageSize,
                          categoryKey,
                          "",
                        );

                        // Hide calendar after clearing
                        setIsDateDropdownOpen(false);
                      }}
                    >
                      Set Semula
                    </Button>
                  </div>
                </div>
              </div>
            )}
          >
            <Button
              icon={renderArrowDownIcon()}
              type="primary"
              size="large"
              className="min-w-48"
              iconPlacement="end"
            >
              {selectedDate
                ? dayjs(selectedDate).format("DD MMM YYYY")
                : "Tapis mengikut tarikh"}
            </Button>
          </Dropdown>

          <Dropdown
            trigger={["click"]}
            menu={{
              items: CATEGORY_ITEMS,
              onClick: ({ key }) => {
                handleCategoryChange(key);
              },
            }}
          >
            <Button
              icon={renderArrowDownIcon()}
              type="primary"
              size="large"
              className="min-w-48"
              iconPlacement="end"
            >
              {selectedCategoryLabel}
            </Button>
          </Dropdown>

          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari"
            size="large"
            prefix={
              <Search
                size={16}
                style={{ color: "#C2C2C2" }}
                aria-hidden="true"
              />
            }
            className="max-w-2xs"
          />
          <Button
            size="large"
            className="min-w-48"
            icon={<RotateCcw size={18} aria-hidden="true" />}
            style={{ color: "#0081B3" }}
            onClick={handleResetFilters}
          >
            Set Semula
          </Button>
        </div>
      </div>

      <div className="mt-6">
        <RejectApplicationTable
          applications={applicationRejectedList?.applications ?? []}
          loading={loading}
          totalCount={applicationRejectedList?.totalCount ?? 0}
          currentPage={currentPage}
          pageSize={pageSize}
          resetKey={tableResetKey}
          onPageChange={handlePageChange}
          onRefresh={() =>
            fetchRejectedList(
              searchValue,
              currentPage,
              pageSize,
              categoryKey,
              selectedDate ?? "",
            )
          }
        />
      </div>
      </section>
    </>
  );
}
